import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchOrganisations } from "../api/organisations";
import { isGlobalManager } from "../lib/forms/formSource";
import { useAuth } from "./AuthProvider";

const SELECTED_ORGANISATION_KEY = "chmforms.admin.organisation.v1";

export type WorkspaceOrganisation = {
  id: number;
  name: string;
  slug: string;
};

type AdminWorkspaceContextValue = {
  organisations: WorkspaceOrganisation[];
  selectedOrganisationId: number | null;
  selectedOrganisation: WorkspaceOrganisation | null;
  isUnassignedSelected: boolean;
  isGrantedSelected: boolean;
  isServiceFormsSelected: boolean;
  canViewGrantedForms: boolean;
  isLoadingOrganisations: boolean;
  selectOrganisation: (organisationId: number | null) => void;
  selectGrantedForms: () => void;
  selectServiceForms: () => void;
};

const AdminWorkspaceContext = createContext<AdminWorkspaceContextValue | null>(
  null,
);

type WorkspaceSelection = number | "unassigned" | "service" | "granted" | null;

function readSelectedOrganisation(): WorkspaceSelection {
  try {
    const raw = localStorage.getItem(SELECTED_ORGANISATION_KEY);
    if (!raw) return null;
    if (raw === "unassigned" || raw === "service" || raw === "granted") {
      return raw;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  } catch {
    return null;
  }
}

export function AdminWorkspaceProvider({ children }: { children: ReactNode }) {
  const { roles, organisations: memberships, grantedFormIds } = useAuth();
  const globalManager = isGlobalManager(roles);
  const canViewServiceForms = roles.includes("admin");
  const canViewGrantedForms =
    grantedFormIds.length > 0 || (!globalManager && memberships.length === 0);
  const [catalog, setCatalog] = useState<WorkspaceOrganisation[]>(() =>
    memberships.map(({ id, name, slug }) => ({ id, name, slug })),
  );
  const [selection, setSelection] = useState<WorkspaceSelection>(
    readSelectedOrganisation,
  );
  const [isLoadingOrganisations, setLoadingOrganisations] =
    useState(globalManager);

  useEffect(() => {
    if (!globalManager) {
      setCatalog(memberships.map(({ id, name, slug }) => ({ id, name, slug })));
      setLoadingOrganisations(false);
      return;
    }

    let cancelled = false;
    setLoadingOrganisations(true);

    void fetchOrganisations()
      .then((items) => {
        if (!cancelled) {
          setCatalog(items.map(({ id, name, slug }) => ({ id, name, slug })));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatalog(
            memberships.map(({ id, name, slug }) => ({ id, name, slug })),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOrganisations(false);
      });

    return () => {
      cancelled = true;
    };
  }, [globalManager, memberships]);

  useEffect(() => {
    if (isLoadingOrganisations) return;

    if (selection === "unassigned" && globalManager) return;
    if (selection === "service" && canViewServiceForms) return;
    if (selection === "granted" && canViewGrantedForms) return;
    if (
      typeof selection === "number" &&
      catalog.some((organisation) => organisation.id === selection)
    ) {
      return;
    }

    setSelection(
      catalog[0]?.id ??
        (globalManager ? "unassigned" : canViewGrantedForms ? "granted" : null),
    );
  }, [
    canViewGrantedForms,
    canViewServiceForms,
    catalog,
    globalManager,
    isLoadingOrganisations,
    selection,
  ]);

  useEffect(() => {
    try {
      if (selection == null) {
        localStorage.removeItem(SELECTED_ORGANISATION_KEY);
      } else {
        localStorage.setItem(SELECTED_ORGANISATION_KEY, String(selection));
      }
    } catch {
      // Selection remains available for the current page.
    }
  }, [selection]);

  const selectOrganisation = useCallback((organisationId: number | null) => {
    setSelection(organisationId ?? "unassigned");
  }, []);
  const selectGrantedForms = useCallback(() => setSelection("granted"), []);
  const selectServiceForms = useCallback(() => setSelection("service"), []);

  const selectedOrganisationId =
    typeof selection === "number" ? selection : null;
  const isUnassignedSelected = selection === "unassigned";
  const isGrantedSelected = selection === "granted";
  const isServiceFormsSelected = selection === "service";

  const selectedOrganisation = useMemo(
    () =>
      catalog.find(
        (organisation) => organisation.id === selectedOrganisationId,
      ) ?? null,
    [catalog, selectedOrganisationId],
  );

  const value = useMemo<AdminWorkspaceContextValue>(
    () => ({
      organisations: catalog,
      selectedOrganisationId,
      selectedOrganisation,
      isUnassignedSelected,
      isGrantedSelected,
      isServiceFormsSelected,
      canViewGrantedForms,
      isLoadingOrganisations,
      selectOrganisation,
      selectGrantedForms,
      selectServiceForms,
    }),
    [
      catalog,
      selectedOrganisationId,
      selectedOrganisation,
      isUnassignedSelected,
      isGrantedSelected,
      isServiceFormsSelected,
      canViewGrantedForms,
      isLoadingOrganisations,
      selectOrganisation,
      selectGrantedForms,
      selectServiceForms,
    ],
  );

  return (
    <AdminWorkspaceContext.Provider value={value}>
      {children}
    </AdminWorkspaceContext.Provider>
  );
}

export function useAdminWorkspace(): AdminWorkspaceContextValue {
  const context = useContext(AdminWorkspaceContext);
  if (!context) {
    throw new Error(
      "useAdminWorkspace must be used within AdminWorkspaceProvider",
    );
  }
  return context;
}
